import { ReactNode, useState } from 'react';

import { useShallow } from 'zustand/react/shallow';

import { CurrencyIcon } from '@/entities/currency';
import { IPaymentTemplate } from '@/entities/payment/lib/serialize';
import { usePaymentsController } from '@/entities/payment/model';

import { VabsParticipantType, VabsPaymentSystem } from '@/shared/api';
import { Human, LogoIconSmall } from '@/shared/assets';
import { shortText } from '@/shared/lib/utils/text';
import {
  AvatarWithIndicator,
  Button,
  SearchInput,
  Skeleton,
  Typography,
} from '@/shared/ui';

import { MoneyFlowType } from '../../../model/types';
import { useSendMoney } from '../../../model/useSendMoney';
import { useSerializePaymentToForm } from '../send-money-shared/lib';

interface RecipientWrapperProps {
  recipient: IPaymentTemplate;
  children: (params: { onClick: () => void }) => ReactNode;
  setStep: (step: MoneyFlowType) => void;
}

const RecipientWrapper = ({
  recipient,
  children,
  setStep,
}: RecipientWrapperProps) => {
  const {
    updateFormData,
    resetData,
    setStep: setSendStep,
  } = useSendMoney(useShallow((state) => state));
  const { serialize } = useSerializePaymentToForm();
  const data = serialize(recipient);

  return children({
    onClick: () => {
      resetData();
      updateFormData({
        ...data,
      });
      if (data.isInner) {
        setStep('send-inner');
        setSendStep('amount');
      } else {
        setStep(
          recipient.type === VabsParticipantType.B2B
            ? 'send-business'
            : 'send-someone',
        );
      }
    },
  });
};

const RecipientItem = ({
  item,
  setStep,
}: {
  item: IPaymentTemplate;
  setStep: (step: MoneyFlowType) => void;
}) => {
  const isInner = item.paymentSystem === VabsPaymentSystem.Inner;
  const displayedName =
    (isInner
      ? item.name
      : item.firstName
        ? `${item.firstName} ${item.lastName || ''}`
        : item.companyName) || '';
  return (
    <RecipientWrapper recipient={item} setStep={setStep}>
      {({ onClick }) => (
        <Button
          className="flex w-full items-center justify-start gap-x-4 !px-4 !py-2 text-start"
          variant="clean"
          onClick={onClick}
        >
          <AvatarWithIndicator
            defaultAvatar={
              <div className="flex h-10 w-10 items-center justify-center rounded-full bg-base-300">
                <Human className="!h-6 !w-6" />
              </div>
            }
            indicatorClassName="bg-base-500"
            indicator={
              isInner ? (
                <LogoIconSmall className="bg-base-500 [&_svg]:!h-5 [&_svg]:!w-5" />
              ) : (
                <CurrencyIcon
                  ticker={item.currency?.ticker}
                  className="[&_svg]:!h-6 [&_svg]:!w-6"
                />
              )
            }
          />

          <div className="flex flex-col gap-y-1">
            <Typography className="max-w-[220px] truncate">
              {shortText(displayedName, 24)}
            </Typography>
            <Typography variant="14-r" color="text-base-100">
              {isInner ? (
                'FCB account'
              ) : (
                <span className="truncate">
                  {item.currency?.ticker} account: {shortText(item.accountNumber, 16)}
                </span>
              )}
            </Typography>
          </div>
        </Button>
      )}
    </RecipientWrapper>
  );
};

const Content = ({ setStep }: { setStep: (step: MoneyFlowType) => void }) => {
  const [searchValue, setSearchValue] = useState('');
  const { payments, isLoading, onSearch } = usePaymentsController(true);
  return (
    <div className="relative mt-5 flex h-full flex-1 flex-col overflow-hidden">
      <div className="mb-5 px-4">
        <SearchInput
          value={searchValue}
          onSearch={(val) => {
            setSearchValue(val);
            onSearch(val);
          }}
          placeholder="Search recipients"
        />
      </div>

      <div className="no-scrollbar flex-1 overflow-y-auto pb-5">
        {isLoading ? (
          <div className="flex flex-col gap-3 px-4">
            {Array.from({ length: 5 }).map((i, idx) => (
              <div key={idx} className="flex w-full items-center gap-4">
                <Skeleton className="h-10 w-10 rounded-full" />
                <div className="flex-1">
                  <Skeleton className="h-5 w-[50%] rounded-xl" />
                  <Skeleton className="mt-1 h-5 w-[70%] rounded-xl" />
                </div>
              </div>
            ))}
          </div>
        ) : (
          <div className="overflow-hidden">
            {payments.map((recipient) => (
              <RecipientItem
                item={recipient}
                setStep={setStep}
                key={recipient.id}
              />
            ))}
          </div>
        )}
      </div>
      <Button
        className="sticky bottom-1 w-full"
        variant="brand-500"
        size="d-l"
        onClick={() => {
          setStep('add-recipient');
        }}
      >
        + Add recipient
      </Button>
    </div>
  );
};

export const getRecipientsProps = ({
  setStep,
}: {
  setStep: (step: MoneyFlowType) => void;
}) => {
  return {
    title: 'Send money',
    content: <Content setStep={setStep} />,
    modalProps: {
      classNames: {
        wrapper: 'h-dvh overflow-hidden',
      },
      className: 'overflow-hidden',
    },
    animationProps: {
      isFirst: true,
      // className: '!min-h-[600px] max-h-[600px]',
    },
    triggerProps: { asChild: true },
    disableOutsideClick: false,
  };
};
